import type { Dealer } from '@features/dealers/types/dealers.types';
import type { Vehicle } from '@features/vehiclesearch/types/vinvrmsearch.types';
import type { VehicleDerivative } from '@features/vehiclesearch/types/manualsearch.types';
import type { Product } from '@features/products/types/products.types';
import type {
    ConfiguredService,
    ConfiguredAddOn,
} from '@features/service-plans/types/services.types';
import type { PaymentScheduleOptions } from '@features/service-plans/types/payment.types';
import type { Customer } from '@features/customers/types/customers.types';
import type { PaymentSelectionPayload } from './paymentSelectionWire';
import type { ScheduleOverrides } from '../types/quote.types';
import type { SmriqConfirmedVehiclePayload, SmriqPlanSettingsPayload } from './smriqWire';

export * from './paymentSelectionWire';

// quote-service's wire key stays `id` (row identity on the wire); the `www`
// domain type calls it `instanceId`. These translate at the boundary.
export type WireConfiguredService = Omit<ConfiguredService, 'instanceId'> & { id: string };
export type WireConfiguredAddOn = Omit<ConfiguredAddOn, 'instanceId'> & { id: number };

export const toWireService = (service: ConfiguredService): WireConfiguredService => {
    const { instanceId, ...rest } = service;
    return { ...rest, id: instanceId };
};

// quote-service stores price/gross_price as 2dp decimal strings (StoreQuoteRequest requires
// both), so `null`, `''` and any non-finite value are corrupt money, never a legitimate zero.
const toWireMoney = (value: unknown, field: string): number => {
    const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    if (typeof parsed !== 'number' || !Number.isFinite(parsed)) {
        throw new Error(`Malformed money on the quote wire: ${field}`);
    }
    return parsed;
};

export const fromWireService = (service: WireConfiguredService): ConfiguredService => {
    const { id, ...rest } = service;
    return {
        ...rest,
        instanceId: id,
        price: toWireMoney(rest.price, 'services.price'),
        gross_price: toWireMoney(rest.gross_price, 'services.gross_price'),
    };
};

export const toWireAddOn = (addOn: ConfiguredAddOn): WireConfiguredAddOn => {
    const { instanceId, ...rest } = addOn;
    return { ...rest, id: instanceId };
};

// A stored line missing source/mandatory defaults to manual and unlocked.
export const fromWireAddOn = (addOn: WireConfiguredAddOn): ConfiguredAddOn => {
    const { id, ...rest } = addOn;
    return {
        ...rest,
        instanceId: id,
        price: toWireMoney(rest.price, 'add_ons.price'),
        gross_price: toWireMoney(rest.gross_price, 'add_ons.gross_price'),
        source: rest.source ?? 'manual',
        mandatory: rest.mandatory ?? false,
    };
};
// above. `date_overrides` is keyed by row index (0-based, `<max_services`).
export interface ScheduleOverridesPayload {
    start_entry_index: number | null;
    date_overrides: Record<number, string>;
    derivation_fingerprint: string;
}

export const toScheduleOverridesPayload = (
    overrides: ScheduleOverrides | undefined
): ScheduleOverridesPayload | undefined => {
    if (!overrides) {
        return undefined;
    }
    return {
        start_entry_index: overrides.startEntryIndex,
        date_overrides: overrides.dateOverrides,
        derivation_fingerprint: overrides.derivationFingerprint,
    };
};

export const fromScheduleOverridesPayload = (
    payload: ScheduleOverridesPayload | null | undefined
): ScheduleOverrides | undefined => {
    if (!payload) {
        return undefined;
    }
    return {
        startEntryIndex: payload.start_entry_index ?? null,
        dateOverrides: payload.date_overrides ?? {},
        derivationFingerprint: payload.derivation_fingerprint ?? '',
    };
};

export interface QuoteRequest {
    dealer: Dealer;
    vehicle: Vehicle | VehicleDerivative;
    product: Product;
    services: WireConfiguredService[];
    add_ons: WireConfiguredAddOn[];
    payment: PaymentScheduleOptions;
    payment_selection?: PaymentSelectionPayload;
    schedule_overrides?: ScheduleOverridesPayload | null;
    customer: Customer | null | undefined;
    total_cost: number;
    // #766: the estimated first-collection date, a `YYYY-MM-DD` calendar key
    // (never an ISO instant) — `null` when it cannot be established from the
    // confirmed payment selection. See `resolveFirstCollectionDate`.
    estimated_start_date: string | null;
    // Resolved plan id for locked/guided products (Epic 2.4.2). snake_case at
    // the wire boundary; quote-service (Slice C, PR #600) accepts it on
    // store/update. Null when the product has no service-count config.
    plan_id: string | null;
    // Durable reference to the selected product's auto-provisioned Campaign
    // (#639, PRD 12 §4/§6). snake_case at the wire boundary; derived from
    // `product.campaign?.[0]?.id`, mirroring the plan_id thread. Null when
    // the product has no campaign identity available.
    campaign_id: string | null;
    // snake_case at the wire boundary. An opaque provider string, not a UUID.
    service_plan_session_id?: string | null;
    // The vehicle service_plan_session_id was confirmed against.
    smriq_confirmed_vehicle?: SmriqConfirmedVehiclePayload | null;
    // The inputs the saved SMRiQ schedule was generated with, so a resume restores the controls.
    smriq_plan_settings?: SmriqPlanSettingsPayload | null;
    // Present only as true: the dealer is the home-dealer placeholder of an early save.
    dealer_provisional?: boolean;
    // The smr digest of the priced basket. Unruled and dropped by quote-service
    // until its verification slice lands.
    pricing_version: string;
    // The stored version this write was composed against; omitted by a pre-versioning draft.
    expected_version?: number;
}


export interface QuoteListResponse {
    success: boolean;
    data: QuoteResponse['data'][];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    };
}

export interface QuoteResponse {
    success: boolean;
    data: {
        id: string;
        quote_ref: string;
        status: 'live' | 'dead' | 'complete';
        dealer: Dealer;
        vehicle: Vehicle | VehicleDerivative;
        product: Product;
        services: WireConfiguredService[];
        add_ons: WireConfiguredAddOn[];
        payment: PaymentScheduleOptions;
        // Persisted confirmed schedule snapshot (#62). Absent/null for a
        // quote with no confirmed selection yet.
        payment_selection?: PaymentSelectionPayload | null;
        // Persisted start-service choice and date overrides (Epic 2.4.6 slice
        // 3). Absent/null for a quote with no overrides.
        schedule_overrides?: ScheduleOverridesPayload | null;
        customer: Customer;
        total_cost: number;
        // #766: quote-service's `Quote` model casts this to `date:Y-m-d`, so a
        // non-null value arrives as a bare calendar key (`2026-09-05`) — the
        // same shape the write path accepts, which is why a read-modify-write
        // roundtrips. Still routed through `toDateKey` below rather than taken
        // raw: a quote persisted before that cast landed, or read through any
        // other serialiser, can still carry an ISO instant
        // (`2026-09-05T00:00:00.000000Z`), and `toDateKey` takes the leading
        // calendar date verbatim without ever constructing a `Date`.
        estimated_start_date: string | null;
        // Resolved plan id persisted by quote-service. Absent/null when no
        // plan resolves.
        plan_id?: string | null;
        // Durable campaign reference persisted by quote-service (#639).
        // Absent/null when the quote's product has no campaign identity.
        campaign_id?: string | null;
        // Absent or null when the quote was never generated from a provider session.
        service_plan_session_id?: string | null;
        // Absent or null when no confirmed-vehicle snapshot is stored.
        smriq_confirmed_vehicle?: SmriqConfirmedVehiclePayload | null;
        // Absent or null when no plan settings are stored (a plan generated before this shipped).
        smriq_plan_settings?: SmriqPlanSettingsPayload | null;
        // Optimistic-concurrency token, required on every response; a write composed without it is unguarded.
        version: number;
        created_at: string;
        updated_at: string;
    };
    message?: string;
    errors?: string[];
    meta?: {
        current_page: number;
        per_page: number;
        total: number;
        last_page: number;
        from: number | null;
        to: number | null;
    };
}
